import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../redux/store";
import PermissionGate from "../../core/layouts/permission-gate";
import ToastComponent from "../../components/toast";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../redux/features/common-slice";
import {
  getUsersByTeamState,
  UserRole,
  usersByTeamStateAction,
  UserResponse,
  getUsersByGroupTeamState,
  usersByGroupTeamStateAction,
} from "../../redux/features/user-slice";
import Table from "../../components/table";
import TableBodyCell from "../../components/table-body-cell";
import EmptyDataOverlay from "../../components/empty-data-overlay";
import Loading from "../../components/loading";
import { adminCat } from "../../core/utils/constants";
import paths from "../../navigation/paths";

export default function GroupTeamsPage() {
  const navigate = useNavigate();

  const { id, name } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const usersByGroupTeamState = useSelector(getUsersByGroupTeamState);
  const errorState = useSelector(getErrorState);

  const fetchUsersByGroupTeam = useCallback(async () => {
    if (id) {
      await dispatch(
        usersByGroupTeamStateAction({ groupTeamId: id as string })
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchUsersByGroupTeam();
  }, [fetchUsersByGroupTeam]);

  useEffect(() => {
    if (name && id) {
      dispatch(
        setBreadcrumbState([
          { id: "teams", path: null, title: "Teams" },
          { id: `team-${id}`, path: null, title: name },
        ])
      );
    }
  }, [dispatch, name, id]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate categories={[]} roles={[]}>
      <Helmet title={name ? name : "Teams"} />
      <Table
        headerCells={[
          { id: "name", label: "Name", align: "text-left", width: "w-3/12" },
          {
            id: "position",
            label: "Position",
            align: "text-left",
            width: "w-3/12",
          },
          {
            id: "customer",
            label: "Customer",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "category",
            label: "Category",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "experience",
            label: "Experience",
            align: "text-left",
            width: "w-2/12",
          },
          // {
          //   id: "role",
          //   label: "Role",
          //   align: "text-left",
          //   width: "w-1/12",
          // },
        ]}
        body={
          usersByGroupTeamState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : usersByGroupTeamState.data.length > 0
            ? usersByGroupTeamState.data.map((member: UserResponse) => {
                return (
                  <tr
                    key={member.id}
                    className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                    onClick={() =>
                      navigate(
                        `${paths.MEMBER}/${member.id}/${
                          member.category.id
                        }?tab=${encodeURIComponent(
                          "Daily Reports"
                        )}&tabType=${encodeURIComponent("1:1 Weekly Reports")}`
                      )
                    }
                  >
                    <TableBodyCell exClassName="font-medium">
                      <label className="whitespace-normal break-words hover:underline cursor-pointer">
                        {member.name}
                      </label>
                      <br />
                      <label className="text-text-secondary text-xs font-light italic">
                        {member.email}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words hover:underline cursor-pointer">
                        {member.position.name}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {member.customer?.name ?? ""}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {member.category?.title ?? ""}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {member.experience.title}
                      </label>
                    </TableBodyCell>
                    {/* <TableBodyCell>
                      <label className="whitespace-normal break-words capitalize">
                        {member.role}
                      </label>
                    </TableBodyCell> */}
                  </tr>
                );
              })
            : [
                <tr key="empty-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Users found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="members" />
    </PermissionGate>
  );
}
